import { useWriteContract, useWaitForTransactionReceipt, useSwitchChain, useAccount } from 'wagmi'
import { parseUnits } from 'viem'
import { BSC_USDT_ADDRESS, BSC_CHAIN_ID } from '@/constants/addresses'
import { ERC20_ABI } from '@/constants/abis'

/**
 * Hook para enviar USDT desde la wallet conectada en BSC
 * Usado para pagos manuales desde la wallet de tesorería
 */
export function useSendUSDT() {
  const { chainId } = useAccount()
  const { switchChainAsync } = useSwitchChain()

  const {
    data: hash,
    writeContractAsync,
    isPending: isWriting,
    error: writeError,
    reset,
  } = useWriteContract()

  const {
    isLoading: isConfirming,
    isSuccess,
    error: confirmError,
  } = useWaitForTransactionReceipt({
    hash,
  })

  /**
   * Envía USDT a un destinatario
   * @param recipient Dirección del destinatario
   * @param amount Cantidad de USDT (ya formateada, ej: "100.5")
   */
  const sendUSDT = async (recipient: string, amount: string): Promise<string | undefined> => {
    try {
      // Verificar que estamos en BSC, si no, cambiar de red
      if (chainId !== BSC_CHAIN_ID) {
        await switchChainAsync({ chainId: BSC_CHAIN_ID })
      }

      // Convertir amount a wei (USDT tiene 18 decimales en BSC)
      const amountWei = parseUnits(amount, 18)

      const txHash = await writeContractAsync({
        address: BSC_USDT_ADDRESS,
        abi: ERC20_ABI,
        functionName: 'transfer',
        args: [recipient as `0x${string}`, amountWei],
        chainId: BSC_CHAIN_ID,
      })

      return txHash
    } catch (error) {
      console.error('Error sending USDT:', error)
      throw error
    }
  }

  return {
    sendUSDT,
    hash,
    isWriting,
    isConfirming,
    isSuccess,
    isPending: isWriting || isConfirming,
    error: writeError || confirmError,
    reset,
  }
}
